import React from 'react';
import {
    Responsive,
    SimpleList,
    Datagrid,
    TextField,
    SimpleShowLayout,
    SimpleForm,
    TextInput,
    LongTextInput,
    ReferenceInput,
    required,
    email
} from 'admin-on-rest';
import List from '../admin/List';
import Create from '../admin/Create';
import Show from '../admin/Show';
import Edit from '../admin/Edit';
import AdditionalTaxesTable from '../components/AdditionalTaxesTable';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import ViewButton from '../buttons/ViewButton';
import EditButton from '../buttons/EditButton';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {validateRequiredSelect} from '../shared/formFunctions';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/maps/layers';

export const fuseDbaIcon = Icon;

// Handle event from Add Additional Tax
const handleAddTax = (e, index, value) => {
};

// Handle event from Remove Additional Tax
const handleRemoveTax = (e) => {
};

const MakeAdditionalTaxesTable = ({record, showButtons, clearList}) => {
    return (
        <AdditionalTaxesTable
            {...record}
            clearList={clearList}
            showAdd={showButtons.add}
            showRemove={showButtons.remove}
            onAddUpdate={handleAddTax}
            onRemoveUpdate={handleRemoveTax}
        />
    )
};

/*
 * View: List
 */

// Set title for List Locations
const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>DBAs</span>;

const ListDescription = () => {
    return (
        <span>
            <p>Locations use <strong>DBA</strong> to display the correct Product Types on the POS System. A DBA holds
                the Business Name and Product Type, and is used for Locations to display products of that Product Type
                on the POS System.</p>
            <p>There are instances in the Seasonal Retail Industry where you might have one DBA for a Fireworks
                business and a DBA for a Halloween Costume Business. Instead of managing all Locations under the same
                DBA, you can have multiple DBAs with different Product Types so employees will be able to sell the correct
                Products in your Stores.</p>
            <p>Note: If you have not created a <strong>Product Type</strong> first, please do so before creating a
                DBA.</p>
            <img src="img-diagram-DBA-List.png" alt="Diagram of Locations - DBAs" style={viewStyles.diagram}/>
        </span>
    );
};

// Description View Only
const ListDescriptionViewOnly = () => {
    return (
        <span>
            <p>Locations use <strong>DBA</strong> to display the correct Product Types on the POS System. A DBA holds
                the Business Name and Product Type, and is used for Locations to display products of that Product Type
                on the POS System.</p>
            <p>There are instances in the Seasonal Retail Industry where you might have one DBA for a Fireworks
                business and a DBA for a Halloween Costume Business. Instead of managing all Locations under the same
                DBA, you can have multiple DBAs with different Product Types so employees will be able to sell the correct
                Products in your Stores.</p>
            <img src="img-diagram-DBA-List.png" alt="Diagram of Locations - DBAs" style={viewStyles.diagram}/>
        </span>
    );
};

/*
 * List DBAs table Full Access
 */
export const fuseLocationDbaList = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescription/>} sort={{field: 'name', order: 'ASC'}}>
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.name}
                    secondaryText={record => record.product_type}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="name" label="DBA Name"/>
                    <TextField source="id" label="DBA ID"/>
                    <TextField source="product_type" label="Product Type"/>
                    <TextField source="price_level.name" label="Price Level" sortable={false}/>
                    <ViewButton/>
                    <EditButton/>
                </Datagrid>
            }
        />
    </List>
);

/*
 * List DBAs table View Only Access
 */
export const fuseLocationDbaListViewOnly = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescriptionViewOnly/>} sort={{field: 'name', order: 'ASC'}}>
        <Responsive
            small={
                <SimpleList
                    primaryText={record => record.name}
                    secondaryText={record => record.product_type}
                />
            }
            medium={
                <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                    <TextField source="name" label="DBA Name"/>
                    <TextField source="id" label="DBA ID"/>
                    <TextField source="product_type" label="Product Type"/>
                    <TextField source="price_level.name" label="Price Level" sortable={false}/>
                    <ViewButton/>
                </Datagrid>
            }
        />
    </List>
);

/*
 * View: Show
 */

// Set title for Show DBA
const ShowTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>{record ? `${record.name}` : 'DBA'}</span>;
};

const ShowDescription = () => {
    return (
        <span>
            <p>Review DBA details below. To edit this DBA, click <strong>Edit</strong> above.</p>
        </span>
    );
};

/*
 * Show DBA Table
 */
export const fuseLocationDbaShow = (props) => (
    <Show title={<ShowTitle/>} desc={<ShowDescription/>} {...props}>
        <SimpleShowLayout>
            <TextField source="name" label="DBA Name"/>
            <TextField source="id" label="DBA ID"/>
            <TextField source="telephone" label="Phone"/>
            <TextField source="email" label="Email"/>
            <TextField source="product_type" label="Product Type"/>
            <TextField source="price_level.name" label="Price Level"/>
            <TextField source="receipt_message" label="Receipt Message" style={viewStyles.fieldWrap}/>
            <span style={viewStyles.fieldHeader}>Additional Taxes</span>
            {<MakeAdditionalTaxesTable
                showButtons={{'add': false, 'remove': false}}
                clearList={false}
            />}
        </SimpleShowLayout>
    </Show>
);

/*
 * View: Create form
 */

// Set title for Create DBA
const CreateTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>Create a DBA</span>;

const CreateDescription = () => {
    return (
        <span>
            <p>Locations use <strong>DBA</strong> to display the correct Product Types on the POS System. A DBA holds
                the Business Name and Product Type, and is used for Locations to display products of that Product Type
                on the POS System.</p>
            <p>If you have not created a Product Type first, please do so before creating a DBA.</p>
            <p>Enter the required fields below.</p>
        </span>
    );
};

const defaultCreateValues = {
    additional_taxes: [],
    additional_tax_objects: [],
    receipt_message: ''
};


/*
 * Create DBA form
 */
export const fuseLocationDbaCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm redirect="list" defaultValue={defaultCreateValues}>
            <TextInput source="name" label="Name of DBA" validate={[required]}/>
            <span style={viewStyles.fieldDescription}>
                Enter the <strong>Name</strong> of the DBA.
                <TooltipIcon dataFor="ttName"/>
                <ReactTooltip id="ttName" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    This is the name of your business DBA for a specific Product Type. Most stores will have a single
                    Product Type.
                </ReactTooltip>
            </span>

            <TextInput source="telephone" label="Phone" validate={[required]}/>

            <TextInput source="email" label="Email" type="email" validate={[required, email]}/>

            <ReferenceInput
                label="Select a Product Type*"
                source="product_type"
                reference="ProductTypes"
                validate={[required, validateRequiredSelect]}
                perPage={10000}
                allowEmpty>
                <VirtualizedSelectInput
                    addField={true}
                    optionText="name"
                    optionValue="name"
                    errorMessage="Product Type is required."
                    required={true}
                    options={{
                        clearable: false,
                        labelKey: "name",
                        placeholder: "Select a Product Type*",
                        required: true,
                    }}
                />
            </ReferenceInput>
            <span style={viewStyles.fieldDescription}>
                Select a <strong>Product Type</strong>.
                <br/>
                (If Type is not available, please exit and go
                    to <a href="#/ProductTypes/create" style={viewStyles.fieldLink}>Add Product Type</a>).
                <TooltipIcon dataFor="ttType"/>
                <ReactTooltip id="ttType" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Product Type</strong> is used for configuring the POS System to display products to be
                    sold dependent on your Location's DBA. In most cases, a business will only have one Product Type.
                </ReactTooltip>
            </span>

            <ReferenceInput
                label="Select a Price Level"
                source="price_level.name"
                reference="PriceLevels"
                allowEmpty>
                <VirtualizedSelectInput
                    addField={true}
                    optionText="name"
                    optionValue="name"
                    label="Select a Price Level"
                    options={{
                        clearable: false,
                        labelKey: "name",
                        placeholder: "Select a Price Level",
                    }}
                />
            </ReferenceInput>
            <span style={viewStyles.fieldDescription}>
                Select a <strong>Price Level</strong>.
                <br/>
                (If Price Level is not available, please exit and go
                    to <a href="#/PriceLevels/create" style={viewStyles.fieldLink}>Add Price Level</a>).
                <TooltipIcon dataFor="ttLevel"/>
                <ReactTooltip id="ttLevel" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A named <strong>Price Level</strong> is used for unique Product pricing of specific Physical
                    Locations or Locations grouped by DBA.
                    <br/><br/>
                    A Price Level labels a Price Level Price of a Product. Certain Locations or DBAs could have
                    different pricing than others. If a Location or DBA has a Price Level, the POS System will display
                    the Price Level Price fo a Product instead of its default price.
                </ReactTooltip>
            </span>

            <LongTextInput source="receipt_message" label="Receipt Message"/>
            <span style={viewStyles.fieldDescription}>
                If desired, enter a <strong>Receipt Message</strong>.
                <TooltipIcon dataFor="ttReceipt"/>
                <ReactTooltip id="ttReceipt" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Receipt Message</strong> is additional copy that will appear on printed receipts. This
                    may be a message specific to receipts printed in a particular state or city. Do not include HTML,
                    links or scripts in this field.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldHeader}>Additional Taxes</span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            <span style={viewStyles.fieldDescription}>
                    If one or more <strong>Additional Taxes</strong> are required by law for this DBA,
                    add them below. (If a desired Additional Tax is not available, please exit and go
                    to <a href="#/AdditionalTaxes/create" style={viewStyles.fieldLink}>Add Additional Tax</a>).
                </span>
            {<MakeAdditionalTaxesTable
                showButtons={{'add': true, 'remove': true}}
                clearList={true}
            />}
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the DBAs List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Create>
);

// Set title for Edit DBA
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit {record ? `${record.name}` : 'DBA'}</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>Modify DBA fields below. Click <strong>Save</strong> to store this DBA.</p>
        </span>
    );
};

/*
 * Edit DBA form
 */
export const fuseLocationDbaEdit = (props) => (
    <Edit {...props} title={<EditTitle/>} desc={<EditDescription/>}>
        <SimpleForm>
            <TextInput source="name" label="Name of DBA" validate={[required]}/>

            <TextInput source="telephone" label="Phone" validate={[required]}/>

            <TextInput source="email" label="Email" type="email" validate={[required, email]}/>

            <ReferenceInput
                label="Select a Product Type*"
                source="product_type"
                reference="ProductTypes"
                validate={[required, validateRequiredSelect]}
                allowEmpty>
                <VirtualizedSelectInput
                    addField={true}
                    optionText="name"
                    optionValue="name"
                    errorMessage="Product Type is required."
                    required={true}
                    options={{
                        clearable: false,
                        labelKey: "name",
                        placeholder: "Select a Product Type*",
                        required: true,
                    }}
                />
            </ReferenceInput>
            <span style={viewStyles.fieldDescription}>
                Select a <strong>Product Type</strong>.
                <br/>
                (If Type is not available, please exit and go
                    to <a href="#/ProductTypes/create" style={viewStyles.fieldLink}>Add Product Type</a>).
                <TooltipIcon dataFor="ttType"/>
                <ReactTooltip id="ttType" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Product Type</strong> is used for configuring the POS System to display products to be
                    sold dependent on your Location's DBA. In most cases, a business will only have one Product Type.
                </ReactTooltip>
            </span>

            <ReferenceInput
                label="Price Level"
                source="price_level.name"
                reference="PriceLevels"
                allowEmpty>
                <VirtualizedSelectInput
                    addField={true}
                    optionText="name"
                    optionValue="name"
                    label="Price Level"
                    options={{
                        clearable: false,
                        labelKey: "name",
                        placeholder: "Price Level",
                    }}
                />
            </ReferenceInput>
            <span style={viewStyles.fieldDescription}>
                Select a <strong>Price Level</strong>.
                <br/>
                (If Price Level is not available, please exit and go
                    to <a href="#/PriceLevels/create" style={viewStyles.fieldLink}>Add Price Level</a>).
                <TooltipIcon dataFor="ttLevel"/>
                <ReactTooltip id="ttLevel" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A named <strong>Price Level</strong> is used for unique Product pricing of specific Physical
                    Locations or Locations grouped by DBA.
                    <br/><br/>
                    A Price Level labels a Price Level Price of a Product. Certain Locations or DBAs could have
                    different pricing than others. If a Location or DBA has a Price Level, the POS System will display
                    the Price Level Price fo a Product instead of its default price.
                </ReactTooltip>
            </span>

            <LongTextInput source="receipt_message" label="Receipt Message"/>
            <span style={viewStyles.fieldDescription}>
                If desired, enter or modify a <strong>Receipt Message</strong>.
                <TooltipIcon dataFor="ttReceipt"/>
                <ReactTooltip id="ttReceipt" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Receipt Message</strong> is additional copy that will appear on printed receipts. This
                    may be a message specific to receipts printed in a particular state or city. Do not include HTML,
                    links or scripts in this field.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldHeader}>Additional Taxes</span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            <span style={viewStyles.fieldDescription}>
                    If one or more <strong>Additional Taxes</strong> are required by law for this DBA,
                    add them below. (If a desired Additional Tax is not available, please exit and go
                    to <a href="#/AdditionalTaxes/create" style={viewStyles.fieldLink}>Add Additional Tax</a>).
                </span>
            {<MakeAdditionalTaxesTable
                showButtons={{'add': true, 'remove': true}}
                clearList={false}
            />}
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the DBAs List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/Location/dba.js